import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import Login from './pages/Login';
import Portal from './pages/Portal';
import OperatorDashboard from './pages/OperatorDashboard';
import TechnicianDashboard from './pages/TechnicianDashboard';
import AdminDashboard from './pages/AdminDashboard';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import { Toaster, toast } from 'sonner';
import { initializeNotifications, onForegroundMessage, unregisterToken } from './lib/firebase';
import './App.css';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Logout function - useCallback to avoid recreating on every render
  const handleLogout = useCallback(async () => {
    // Unregister FCM token
    await unregisterToken();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    toast.info('Sessiyanız başa çatdı, yenidən daxil olun');
  }, []);

  // Setup axios interceptor for 401 handling
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Token expired or invalid - logout user
          console.log('Token expired - logging out');
          handleLogout();
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [handleLogout]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      setUser(JSON.parse(userData));
    }
    setLoading(false);
  }, []);

  // Initialize notifications when user is logged in
  useEffect(() => {
    if (user) {
      // Initialize push notifications
      initializeNotifications().then((success) => {
        if (success) {
          console.log('Push notifications initialized');
        }
      }).catch(err => {
        console.log('Push notifications not available:', err);
      });

      // Listen for foreground messages
      const unsubscribe = onForegroundMessage((payload) => {
        const title = payload.notification?.title || 'Bildiriş';
        const body = payload.notification?.body || '';
        
        // Show toast notification
        toast.info(title, {
          description: body,
          duration: 5000,
          action: {
            label: 'Bax',
            onClick: () => {
              // Refresh the page to see new data
              window.location.reload();
            }
          }
        });
      });

      return () => {
        if (unsubscribe) unsubscribe();
      };
    }
  }, [user]);

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Yüklənir...</div>;
  }

  return (
    <>
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <Routes>
          {/* Portal - public route */}
          <Route path="/portal" element={<Portal />} />
          
          <Route path="/" element={!user ? <Login onLogin={handleLogin} /> : <Navigate to={`/${user.role}`} />} />
          <Route 
            path="/operator" 
            element={user && user.role === 'operator' ? <OperatorDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/" />} 
          />
          <Route 
            path="/technician" 
            element={user && user.role === 'technician' ? <TechnicianDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/" />} 
          />
          <Route 
            path="/admin" 
            element={user && user.role === 'admin' ? <AdminDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/" />} 
          />
          <Route 
            path="/accountant" 
            element={user && user.role === 'accountant' ? <AdminDashboard user={user} onLogout={handleLogout} readOnly={true} /> : <Navigate to="/" />} 
          />
          <Route 
            path="/superadmin" 
            element={user && (user.role === 'superadmin' || user.is_superadmin) ? <SuperAdminDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/" />} 
          />
        </Routes>
      </BrowserRouter>
    </>
  );
}

export default App;
